import React from 'react';
import formatter from '@payit/payit-formatter';
import PropTypes from 'prop-types';
import { Text, } from '@payit/cool-web-ui-library';
import SearchBar from '../../../searchbar/SearchBar';
import TransactionService from '../../../../service/TransactionService';
import ObjectUtils from '../../../../modules/ObjectUtils';
import VerticalLabelList from '../../../label/VerticalLabelList';
import FieldSet from '../../../fieldset/FieldSet';
import Numbers from '../../../../modules/Numbers';
import Strings from '../../../../modules/StringUtils';

class Preview extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isEmptySearch: false,
    };
  }

  onClick() {
    const { forward, } = this.props;
    forward();
  }

  onDetailsClick(data) {
    const { search, } = data;
    this.setState({ isValid: false, isCashTransaction: false, });

    const reducer = res => res?.data;
    const isEmptySearch = (a1 = {}, a2 = {}) => {
      const { length, } = Object.keys(a1);
      return length === 0 && Object.keys(a2).length === 0;
    };
    const isRefunded = (paymentHistory = []) => paymentHistory.filter(item => item.action.includes('REFUND')).length > 0;
    const isChargeback = (paymentHistory = []) => paymentHistory.filter(item => item.action.includes('CHARGEBACK')).length > 0;
    const isGenResolve = (lineItems = []) => lineItems.filter(item => item.serviceOfferingId.includes('gen_resolve')).length > 0;
    const isCashTransaction = (transaction) => transaction && transaction.paymentAccountType === 'CASH';
    const returnMatch = (v2 = {}, v3 = {}) => {
      if (Object.keys(v2).length > 0) {
        const transaction = {
          ...v2,
        };
        const paymentHistory = ObjectUtils.formatStringAsJSONArray(transaction.paymentHistory);
        const lineItems = ObjectUtils.formatStringAsJSONArray(transaction.lineItems);
        return {
          version: 2,
          ...transaction,
          transactionFees: Numbers.toNumber(transaction.serviceFeeAmount),
          isRefunded: isRefunded(paymentHistory),
          hasChargeback: isChargeback(paymentHistory),
          isGenResolve: isGenResolve(lineItems),
        };
      }

      if (Object.keys(v3).length > 0) {
        const paymentHistory = ObjectUtils.formatStringAsJSONArray(v3.paymentHistory);
        const lineItems = ObjectUtils.formatStringAsJSONArray(v3.lineItems);

        // Fees is a Map in Mongo so if there are multiple fees, then warehouse has it in
        // individual columns named for Fee Type i.e. fees_PAYIT_FEE
        // with column content {"value":"2.000000","currencyCode":"USD"}.
        // For single fees, the fee is in column named fees with Fee Type included in content i.e.
        // {"CREDIT_CARD":{"value":"3.580000","currencyCode":"USD"}}
        const fees = ObjectUtils.formatStringAsJSONArray('[]');
        if (v3.fees == null) {
          Object.keys(v3).filter(key => key.startsWith('fees_')).forEach(fee => (v3[fee] == null ? '' : fees.push(ObjectUtils.formatStringAsJSONObject(v3[fee]))));
        } else {
          const _fees = ObjectUtils.formatStringAsJSONObject(v3.fees);
          Object.keys(_fees).map(key => fees.push(_fees[key]));
        }
        v3.lineItems.forEach(lineItem => {
          if (lineItem.fees) {
            const _fees = ObjectUtils.formatStringAsJSONObject(lineItem.fees);
            Object.keys(_fees).map(key => fees.push(_fees[key]));
          }
        });

        const chargedAmount = Strings.formatCurrency({
          amount: Numbers.addAll(
            v3.lineItems.map(item => parseFloat(item.amount.replace(/\$/g, '')))
          ),
          currencyCode: 'USD',
        });
        const transactionFees = Strings.formatCurrency({
          amount: String(Object.values(fees).reduce((acc, fee) => {
            return acc + parseFloat(fee.amount.replace(/\$/g, ''));
          }, 0)),
          currencyCode: 'USD',
        });

        return {
          version: 3,
          ...v3,
          chargedAmount,
          transactionFees,
          isRefunded: isRefunded(paymentHistory),
          hasChargeback: isChargeback(paymentHistory),
          isGenResolve: isGenResolve(lineItems),
        };
      }
      return {
        version: null,
        transaction: null,
      };
    };
    let isCash;
    let isEmpty;

    Promise.all([
      TransactionService.getTransactionDetail(search.value).then(reducer),
      TransactionService.getV3TransactionDetail(search.value).then(reducer),
    ])
      .then(([ v2TransactionsResponse, v3TransactionsResponse, ]) => {
        const { transaction, } = v2TransactionsResponse || { transaction: {}, };
        const { v3transaction, } = v3TransactionsResponse || { v3transaction: {}, };
        isCash = isCashTransaction(v3transaction);
        isEmpty = isEmptySearch(transaction, v3transaction);
        if (!isEmpty) {
          const value = returnMatch(transaction, v3transaction);
          this.setState({
            transaction: value,
          });
        } else {
          this.setState({
            isEmptySearch: isEmpty,
          });
        }
      })
      .finally(() => {
        this.setState({
          isValid: !isEmpty && !isCash,
          isCashTransaction: isCash,
        });
        data.kill();
      });
  }

  displayWarningMessage(message) {
    return (
      <div className="mt-3">
        <Text className="text-danger">{message}</Text>
      </div>
    );
  }

  determineStatusToDisplay(transaction) {
    if (transaction.paymentAccountType === 'CARD_PRESENT') {
      return transaction.ccStatus;
    }

    if (transaction.paymentAccountType === 'CASH') {
      return ObjectUtils.formatStringAsJSONObject(transaction.cashData).cashStatus;
    }

    if (transaction.paymentAccountType === 'CREDIT_CARD') {
      return transaction.ccdata_ccstatus ? transaction.ccdata_ccstatus : transaction.ccStatus;
    }
    if (transaction.paymentAccountType === 'BANK') {
      return transaction.achStatus
        ? transaction.achStatus : ObjectUtils.formatStringAsJSONObject(transaction.achData).achStatus || transaction.status;
    }
    return transaction.errorMessage;
  }

  getSOIDToDisplayV3(transaction) {
    const lineItems = transaction.lineItems;
    const serviceOfferingId = new Set(lineItems.map(item => item.serviceOfferingId));
    const soidArray = Array.from(serviceOfferingId);
    return soidArray.join(', ');
  }

  determineSoidToDisplay(transaction) {
    return transaction.version === 3 ? this.getSOIDToDisplayV3(transaction) : transaction.serviceOfferingId;
  }

  formatDetails() {
    const {
      transaction, isValid, isEmptySearch,
    } = this.state;
    const { data, } = this.props;
    if (isValid === false && isEmptySearch) {
      return <p className="small text-danger">Not Found</p>;
    }

    if (!transaction && isValid === undefined) {
      return null;
    }

    if (!transaction) {
      return null;
    }

    data.transaction = transaction;
    data.getDetails = () => {
      const items = VerticalLabelList.toList({
        'Transaction ID': transaction.id,
        Type: transaction.transactionType,
        'Payment Account Type': transaction.paymentAccountType,
        Amount: transaction.chargedAmount,
        Fees: transaction.transactionFees || formatter.money.printMoney(0),
        Status: this.determineStatusToDisplay(transaction)
        || transaction.errorMessage,
        'Service Offering Id(s)': this.determineSoidToDisplay(transaction),
        'Has Refunds': transaction.isRefunded ? 'Yes' : 'No',
        'Has a chargeback': transaction.hasChargeback ? 'Yes' : 'No',
      });

      return (
        <FieldSet title="Details">
          <VerticalLabelList items={items} />
        </FieldSet>
      );
    };

    return data.getDetails();
  }

  render() {
    const { isValid, isCashTransaction, } = this.state;
    const { data, } = this.props;
    const { transaction = {}, } = data;
    return (
      <div className="container-fluid">
        <SearchBar defaultText={transaction.id} onClick={event => this.onDetailsClick(event)} />
        {this.formatDetails()}
        {isCashTransaction && this.displayWarningMessage('Cash transactions cannot be refunded.')}
        {transaction.serviceOfferingId === 'gen_resolve' && this.displayWarningMessage('The current service offering ID is "gen_resolve." Refunding this transaction would create a strange email from our platform. Please consider reverting to the original SOID.')}
        {transaction.isGenResolve === true && this.displayWarningMessage('The current service offering ID is "gen_resolve." Refunding this transaction would create a strange email from our platform. Please consider reverting to the original SOID.')}
        <button
          type="button"
          disabled={!isValid}
          onClick={() => this.onClick()}
          className="btn btn-primary mt-3"
        >
          Continue
        </button>
      </div>
    );
  }
}
Preview.propTypes = {
  forward: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
};
export default Preview;
